import { handleUnauthorized } from '@utils/apiHelpers'
import {
  normalizeSoundTone,
  type SoundTone,
} from '@utils/notificationSounds'

export type { SoundTone }

export type SoundNotificationEvents = {
  /** New visitor appeal in an unaccepted (inbox) dialog */
  new_conversation?: SoundTone
  /** Visitor appeal in an accepted (my) dialog */
  accepted_dialog?: SoundTone
  /** Incoming dialog transfer to this operator */
  dialog_transfer?: SoundTone
}

export type SoundNotificationsSettings = {
  enabled: boolean
  events: SoundNotificationEvents
}

export type WorkspaceSettings = {
  soundNotifications: SoundNotificationsSettings
}

type ApiSuccess<T> = {
  success?: boolean
  data?: T
  error?: string
  message?: string
}

/** Legacy event keys kept for reading older saved settings. */
type LegacySoundNotificationEvents = SoundNotificationEvents & {
  new_message?: SoundTone
  current_dialog_message?: SoundTone
}

type WorkspaceSettingsResponse = {
  settings?: {
    soundNotifications?: Partial<SoundNotificationsSettings> & {
      events?: Partial<LegacySoundNotificationEvents>
    }
  }
}

export const DEFAULT_SOUND_NOTIFICATIONS: SoundNotificationsSettings = {
  enabled: true,
  events: {
    new_conversation: 'sound-2',
    accepted_dialog: 'sound-1',
    dialog_transfer: 'sound-3',
  },
}

const WORKSPACE_SETTINGS_PATH = '/api/operator/workspace/settings'

function trimBase(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/$/, '')
}

export function parseWorkspaceSettings(json: unknown): WorkspaceSettings {
  const root = json as Record<string, any> | null
  const settingsNode = root?.settings ?? root
  const raw =
    settingsNode?.soundNotifications ??
    settingsNode?.sound_notifications ??
    root?.soundNotifications ??
    root?.sound_notifications

  if (!raw || typeof raw !== 'object') {
    return {
      soundNotifications: {
        ...DEFAULT_SOUND_NOTIFICATIONS,
        events: { ...DEFAULT_SOUND_NOTIFICATIONS.events },
      },
    }
  }

  const events = (raw.events ?? {}) as Partial<LegacySoundNotificationEvents>
  const enabledRaw = raw.enabled ?? raw.isEnabled ?? raw.is_enabled
  let enabled = DEFAULT_SOUND_NOTIFICATIONS.enabled
  if (typeof enabledRaw === 'boolean') {
    enabled = enabledRaw
  } else if (typeof enabledRaw === 'number') {
    enabled = enabledRaw !== 0
  } else if (typeof enabledRaw === 'string') {
    const normalized = enabledRaw.trim().toLowerCase()
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') enabled = true
    else if (normalized === 'false' || normalized === '0' || normalized === 'no') enabled = false
  }

  return {
    soundNotifications: {
      enabled,
      events: {
        new_conversation: normalizeSoundTone(
          events.new_conversation,
          DEFAULT_SOUND_NOTIFICATIONS.events.new_conversation ?? 'sound-2',
        ),
        accepted_dialog: normalizeSoundTone(
          events.accepted_dialog ?? events.new_message,
          DEFAULT_SOUND_NOTIFICATIONS.events.accepted_dialog ?? 'sound-1',
        ),
        dialog_transfer: normalizeSoundTone(
          events.dialog_transfer ?? events.current_dialog_message,
          DEFAULT_SOUND_NOTIFICATIONS.events.dialog_transfer ?? 'sound-3',
        ),
      },
    },
  }
}

async function parseApiErrorMessage(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as ApiSuccess<unknown>
    return (
      (typeof data?.error === 'string' && data.error) ||
      (typeof data?.message === 'string' && data.message) ||
      `Request failed (${response.status})`
    )
  } catch {
    return `Request failed (${response.status})`
  }
}

export async function fetchWorkspaceSettings(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
): Promise<WorkspaceSettings> {
  const base = trimBase(apiBaseUrl)
  const response = await fetch(`${base}${WORKSPACE_SETTINGS_PATH}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  })

  if (handleUnauthorized(response)) {
    throw new Error('Unauthorized')
  }

  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response))
  }

  const json = await response.json()
  const data = (json as ApiSuccess<WorkspaceSettingsResponse>)?.data ?? json
  return parseWorkspaceSettings(data)
}

export type PatchWorkspaceSettingsPayload = {
  settings: {
    soundNotifications?: {
      enabled?: boolean
      events?: Partial<SoundNotificationEvents>
    }
  }
}

export async function patchWorkspaceSettings(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  payload: PatchWorkspaceSettingsPayload,
): Promise<WorkspaceSettings> {
  const base = trimBase(apiBaseUrl)
  const response = await fetch(`${base}${WORKSPACE_SETTINGS_PATH}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(payload),
  })

  if (handleUnauthorized(response)) {
    throw new Error('Unauthorized')
  }

  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response))
  }

  const json = await response.json()
  const data = (json as ApiSuccess<WorkspaceSettingsResponse>)?.data ?? json
  return parseWorkspaceSettings(data)
}

export function isOperatorSettingsUpdatedEvent(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false
  const type = String((data as { type?: string }).type ?? '').toLowerCase()
  return type === 'operator.settings.updated'
}
